import { useEffect, useMemo, useRef, useState } from 'react';
import { Pencil, Plus, Trash2, X } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Tabs } from '@/components/ui/Tabs';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Button } from '@/components/ui/Button';
import { Banner } from '@/components/ui/Banner';
import { Badge } from '@/components/ui/StatusBadge';
import { Input, Label, Select } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
import { useConfirm } from '@/components/ui/ConfirmDialog';
import { useAuthStore } from '@/stores/auth';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useHotkey } from '@/hooks/useHotkey';
import { cn } from '@/lib/utils';
import {
  OBJECTIVE_PERIOD_LABELS,
  OBJECTIVE_TYPE_LABELS,
  objectivesApi,
  type CreatableObjectivePeriod,
  type ObjectiveItem,
  type ObjectiveProgress,
  type ObjectiveType,
} from '@/services/objectives.api';
import { configApi, type AdminUser } from '@/services/config.api';

// Types créables via l'UI (toujours les 6 du backend V1 - inchangé).
const CREATABLE_TYPES: ObjectiveType[] = [
  'revenue',
  'signed_opportunities',
  'new_prospects',
  'meetings',
  'quotes',
  'calls',
];
const CREATABLE_PERIODS: CreatableObjectivePeriod[] = ['weekly', 'monthly', 'quarterly', 'yearly'];

// Unité de la cible selon le type - conditionne la mise en forme dans la
// table (k€ vs nombre entier) et le suffixe du champ cible dans les modales.
function targetUnit(type: ObjectiveType): 'euros' | 'count' {
  return type === 'revenue' ? 'euros' : 'count';
}

function formatTarget(type: ObjectiveType, value: number | string): string {
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return String(value);
  if (targetUnit(type) === 'euros') {
    return `${n.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €`;
  }
  return n.toLocaleString('fr-FR');
}

// Libellé auto d'un objectif : « {type} {période} · {cible auto-formatée} ».
function autoLabel(obj: ObjectiveItem): string {
  return `${OBJECTIVE_TYPE_LABELS[obj.type]} · ${OBJECTIVE_PERIOD_LABELS[obj.periodType]} · ${formatTarget(obj.type, obj.targetValue)}`;
}

// ISO au premier lundi <= date (ou date elle-même si lundi) - pour le preset
// « semaine courante ».
function toIsoDate(d: Date): string {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())).toISOString();
}
function startOfWeek(now: Date): string {
  const d = new Date(now);
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() - (day - 1));
  return toIsoDate(d);
}
function endOfWeek(now: Date): string {
  const d = new Date(now);
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + (7 - day));
  return toIsoDate(d);
}
function startOfMonth(now: Date): string {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString();
}
function endOfMonth(now: Date): string {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0)).toISOString();
}
function startOfQuarter(now: Date): string {
  const q = Math.floor(now.getUTCMonth() / 3);
  return new Date(Date.UTC(now.getUTCFullYear(), q * 3, 1)).toISOString();
}
function endOfQuarter(now: Date): string {
  const q = Math.floor(now.getUTCMonth() / 3);
  return new Date(Date.UTC(now.getUTCFullYear(), q * 3 + 3, 0)).toISOString();
}
function startOfYear(now: Date): string {
  return new Date(Date.UTC(now.getUTCFullYear(), 0, 1)).toISOString();
}
function endOfYear(now: Date): string {
  return new Date(Date.UTC(now.getUTCFullYear(), 11, 31)).toISOString();
}
function boundsForPeriod(period: CreatableObjectivePeriod, now = new Date()) {
  switch (period) {
    case 'weekly':
      return { start: startOfWeek(now), end: endOfWeek(now) };
    case 'monthly':
      return { start: startOfMonth(now), end: endOfMonth(now) };
    case 'quarterly':
      return { start: startOfQuarter(now), end: endOfQuarter(now) };
    case 'yearly':
      return { start: startOfYear(now), end: endOfYear(now) };
  }
}

// Transforme un ISO en « YYYY-MM-DD » pour `<input type="date">`.
function toInputDate(iso: string): string {
  return iso.slice(0, 10);
}

// ---------------------------------------------------------------------------
// Onglet principal
// ---------------------------------------------------------------------------

type SubTab = 'active' | 'history';

const SUB_TABS = [
  { key: 'active' as const, label: 'Actifs' },
  { key: 'history' as const, label: 'Historique' },
];

export function ObjectivesTab() {
  const token = useAuthStore((s) => s.accessToken)!;
  const toast = useToast();
  const confirm = useConfirm();
  const [subTab, setSubTab] = useState<SubTab>('active');
  const [items, setItems] = useState<ObjectiveItem[] | null>(null);
  const [progressMap, setProgressMap] = useState<Record<string, ObjectiveProgress | null>>({});
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<ObjectiveItem | null>(null);

  async function load() {
    setError(null);
    try {
      const rows = await objectivesApi.list(token);
      setItems(rows);
      // Chargement des progressions en parallèle (best-effort - une erreur
      // individuelle ne bloque pas le rendu, la cellule affiche juste « - »).
      const results = await Promise.allSettled(
        rows.map((r) => objectivesApi.progress(token, r.id)),
      );
      const map: Record<string, ObjectiveProgress | null> = {};
      rows.forEach((r, i) => {
        const res = results[i];
        map[r.id] = res && res.status === 'fulfilled' ? res.value : null;
      });
      setProgressMap(map);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // « Actif » = periodEnd >= now (objectif en cours ou futur). Historique =
  // periodEnd < now (figé, non modifiable côté backend).
  const now = useMemo(() => new Date(), []);
  const filtered = useMemo(() => {
    if (!items) return null;
    return items.filter((it) => {
      const isActive = new Date(it.periodEnd).getTime() >= now.getTime();
      return subTab === 'active' ? isActive : !isActive;
    });
  }, [items, subTab, now]);

  async function handleDelete(obj: ObjectiveItem) {
    const ok = await confirm({
      title: `Supprimer cet objectif ?`,
      message: autoLabel(obj),
      variant: 'danger',
      confirmLabel: 'Supprimer',
    });
    if (!ok) return;
    try {
      await objectivesApi.delete(token, obj.id);
      toast({ tone: 'success', message: 'Objectif supprimé' });
      await load();
    } catch (err) {
      toast({ tone: 'danger', message: err instanceof Error ? err.message : 'Erreur' });
    }
  }

  const columns: Column<ObjectiveItem>[] = [
    {
      key: 'label',
      header: 'Intitulé',
      render: (o) => (
        <div>
          <div className="font-medium text-brand">{autoLabel(o)}</div>
          <div className="text-xs text-text-muted">
            {o.user
              ? `${o.user.firstName} ${o.user.lastName}`
              : o.teamId
                ? 'Équipe'
                : 'Tous les utilisateurs'}
          </div>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      render: (o) => <Badge tone="neutral">{OBJECTIVE_TYPE_LABELS[o.type]}</Badge>,
    },
    {
      key: 'period',
      header: 'Période',
      render: (o) => (
        <div className="text-xs">
          <div>{OBJECTIVE_PERIOD_LABELS[o.periodType]}</div>
          <div className="text-text-muted">
            {new Date(o.periodStart).toLocaleDateString('fr-FR')} →{' '}
            {new Date(o.periodEnd).toLocaleDateString('fr-FR')}
          </div>
        </div>
      ),
    },
    {
      key: 'target',
      header: 'Cible',
      align: 'right',
      render: (o) => (
        <span className="font-mono text-sm text-brand">{formatTarget(o.type, o.targetValue)}</span>
      ),
    },
    {
      key: 'progress',
      header: 'Progression',
      render: (o) => {
        const p = progressMap[o.id];
        if (!p) return <span className="text-text-muted">-</span>;
        const pct = Math.min(100, Math.max(0, p.progressPct));
        const barColor = pct >= 100 ? 'bg-success' : pct >= 60 ? 'bg-accent' : 'bg-warning';
        return (
          <div className="flex items-center gap-2">
            <div className="h-2 w-24 overflow-hidden rounded-full bg-gray-100">
              <div className={cn('h-full', barColor)} style={{ width: `${pct}%` }} />
            </div>
            <span className="font-mono text-xs text-text-muted">{p.progressPct}%</span>
          </div>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      render: (o) => {
        const isHistory = new Date(o.periodEnd).getTime() < now.getTime();
        if (isHistory) {
          return (
            <span className="text-[11px] text-text-muted" title="Objectif expiré - figé">
              Figé
            </span>
          );
        }
        return (
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              leadingIcon={<Pencil className="h-3.5 w-3.5" />}
              onClick={() => setEditing(o)}
            >
              Modifier
            </Button>
            <Button
              variant="ghost"
              size="sm"
              leadingIcon={<Trash2 className="h-3.5 w-3.5" />}
              onClick={() => void handleDelete(o)}
            >
              Supprimer
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="space-y-4">
      {error && <Banner tone="danger">{error}</Banner>}
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Tabs tabs={SUB_TABS} current={subTab} onChange={setSubTab} />
          <Button
            variant="primary"
            leadingIcon={<Plus className="h-4 w-4" />}
            onClick={() => setCreateOpen(true)}
          >
            Nouvel objectif
          </Button>
        </div>
        <div className="mt-4">
          <DataTable
            columns={columns}
            data={filtered ?? []}
            keyExtractor={(o) => o.id}
            loading={filtered === null}
            empty={{
              title: subTab === 'active' ? 'Aucun objectif actif' : 'Aucun objectif historique',
              description:
                subTab === 'active'
                  ? 'Cliquez « Nouvel objectif » pour en créer un.'
                  : 'Les objectifs dont la période est terminée apparaîtront ici.',
            }}
          />
        </div>
      </Card>
      {createOpen && (
        <CreateObjectiveModal
          onClose={() => setCreateOpen(false)}
          onSaved={() => {
            setCreateOpen(false);
            void load();
          }}
        />
      )}
      {editing && (
        <EditObjectiveModal
          objective={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Modale création
// ---------------------------------------------------------------------------

type TargetKind = 'individual' | 'team' | 'global';

function CreateObjectiveModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const token = useAuthStore((s) => s.accessToken)!;
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [type, setType] = useState<ObjectiveType>('revenue');
  const [periodType, setPeriodType] = useState<CreatableObjectivePeriod>('monthly');
  const [bounds, setBounds] = useState(() => boundsForPeriod('monthly'));
  const [targetValue, setTargetValue] = useState<string>('');
  const [targetKind, setTargetKind] = useState<TargetKind>('individual');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        setUsers(await configApi.listUsers(token));
      } catch {
        setUsers([]);
      }
    })();
  }, [token]);

  // Équipes dérivées de la liste des users (leur champ `team`).
  const teams = useMemo(() => {
    const map = new Map<string, string>();
    users.forEach((u) => {
      if (u.team) map.set(u.team.id, u.team.name);
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [users]);

  // Quand l'utilisateur change de périodType, on re-calcule automatiquement
  // les bornes vers la période courante correspondante (raccourci).
  function onChangePeriod(p: CreatableObjectivePeriod) {
    setPeriodType(p);
    setBounds(boundsForPeriod(p));
  }

  function toggleUser(id: string) {
    setSelectedUserIds((ids) => (ids.includes(id) ? ids.filter((u) => u !== id) : [...ids, id]));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const target = Number(targetValue);
    if (!Number.isFinite(target) || target <= 0) {
      setError('Cible numérique strictement positive requise');
      return;
    }
    if (targetKind === 'individual' && selectedUserIds.length === 0) {
      setError('Sélectionnez au moins un utilisateur');
      return;
    }
    if (targetKind === 'team' && !selectedTeamId) {
      setError('Sélectionnez une équipe');
      return;
    }
    setSubmitting(true);
    try {
      if (targetKind === 'individual' && selectedUserIds.length > 1) {
        await objectivesApi.bulkCreate(token, {
          type,
          targetValue: target,
          periodType,
          periodStart: bounds.start,
          periodEnd: bounds.end,
          userIds: selectedUserIds,
        });
      } else {
        await objectivesApi.create(token, {
          type,
          targetValue: target,
          periodType,
          periodStart: bounds.start,
          periodEnd: bounds.end,
          userId: targetKind === 'individual' ? (selectedUserIds[0] ?? null) : null,
          teamId: targetKind === 'team' ? selectedTeamId : null,
        });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
      setSubmitting(false);
    }
  }

  return (
    <Modal title="Nouvel objectif" onClose={onClose} wide>
      <form onSubmit={submit} className="space-y-5">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <Label htmlFor="obj-type" required>
              Type
            </Label>
            <Select
              id="obj-type"
              value={type}
              onChange={(e) => setType(e.target.value as ObjectiveType)}
            >
              {CREATABLE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {OBJECTIVE_TYPE_LABELS[t]}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label htmlFor="obj-period" required>
              Période
            </Label>
            <Select
              id="obj-period"
              value={periodType}
              onChange={(e) => onChangePeriod(e.target.value as CreatableObjectivePeriod)}
            >
              {CREATABLE_PERIODS.map((p) => (
                <option key={p} value={p}>
                  {OBJECTIVE_PERIOD_LABELS[p]}
                </option>
              ))}
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <Label htmlFor="obj-start">Début</Label>
            <Input
              id="obj-start"
              type="date"
              value={toInputDate(bounds.start)}
              onChange={(e) =>
                setBounds((b) => ({
                  ...b,
                  start: new Date(`${e.target.value}T00:00:00.000Z`).toISOString(),
                }))
              }
            />
          </div>
          <div>
            <Label htmlFor="obj-end">Fin</Label>
            <Input
              id="obj-end"
              type="date"
              value={toInputDate(bounds.end)}
              onChange={(e) =>
                setBounds((b) => ({
                  ...b,
                  end: new Date(`${e.target.value}T23:59:59.999Z`).toISOString(),
                }))
              }
            />
          </div>
        </div>

        <div>
          <Label htmlFor="obj-target" required>
            Cible ({targetUnit(type) === 'euros' ? '€' : 'nombre'})
          </Label>
          <Input
            id="obj-target"
            type="number"
            min={1}
            value={targetValue}
            onChange={(e) => setTargetValue(e.target.value)}
            placeholder={targetUnit(type) === 'euros' ? '10000' : '20'}
          />
        </div>

        <div>
          <Label>Attribué à</Label>
          <div className="mt-1.5 flex gap-2">
            {(
              [
                { v: 'individual', l: 'Individuel' },
                { v: 'team', l: 'Équipe' },
                { v: 'global', l: 'Tous' },
              ] as const
            ).map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => setTargetKind(opt.v)}
                className={cn(
                  'rounded-md border px-3 py-1.5 text-xs font-medium transition-colors',
                  targetKind === opt.v
                    ? 'border-accent bg-accent/10 text-accent'
                    : 'border-border-subtle text-text-muted hover:text-brand',
                )}
              >
                {opt.l}
              </button>
            ))}
          </div>
          {targetKind === 'individual' && (
            <div className="mt-3 max-h-52 overflow-y-auto rounded-md border border-border-subtle bg-white">
              {users.length === 0 ? (
                <div className="p-3 text-xs text-text-muted">Aucun utilisateur disponible.</div>
              ) : (
                users.map((u) => (
                  <label
                    key={u.id}
                    className="flex cursor-pointer items-center gap-2 border-b border-border-subtle px-3 py-2 text-sm hover:bg-gray-50 last:border-b-0"
                  >
                    <input
                      type="checkbox"
                      checked={selectedUserIds.includes(u.id)}
                      onChange={() => toggleUser(u.id)}
                      className="h-4 w-4 rounded border-border text-accent focus:ring-accent/30"
                    />
                    <span className="text-brand">
                      {u.firstName} {u.lastName}
                    </span>
                    <span className="text-xs text-text-muted">{u.email}</span>
                    {u.team && (
                      <span className="ml-auto text-[11px] text-text-muted">{u.team.name}</span>
                    )}
                  </label>
                ))
              )}
            </div>
          )}
          {targetKind === 'team' && (
            <Select
              id="obj-team"
              className="mt-3"
              value={selectedTeamId}
              onChange={(e) => setSelectedTeamId(e.target.value)}
            >
              <option value="">-</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </Select>
          )}
          {targetKind === 'global' && (
            <p className="mt-2 text-xs text-text-muted">
              L&apos;objectif s&apos;appliquera à l&apos;ensemble du périmètre (pas de scope
              collaborateur ni équipe).
            </p>
          )}
        </div>

        {error && <Banner tone="danger">{error}</Banner>}
        <ModalFooter>
          <Button variant="secondary" onClick={onClose} type="button">
            Annuler
          </Button>
          <Button variant="primary" type="submit" loading={submitting}>
            {targetKind === 'individual' && selectedUserIds.length > 1
              ? `Créer ${selectedUserIds.length} objectifs`
              : 'Créer'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Modale édition - uniquement cible + bornes (type/scope figés)
// ---------------------------------------------------------------------------

function EditObjectiveModal({
  objective,
  onClose,
  onSaved,
}: {
  objective: ObjectiveItem;
  onClose: () => void;
  onSaved: () => void;
}) {
  const token = useAuthStore((s) => s.accessToken)!;
  const [targetValue, setTargetValue] = useState<string>(String(objective.targetValue));
  const [start, setStart] = useState<string>(objective.periodStart);
  const [end, setEnd] = useState<string>(objective.periodEnd);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const target = Number(targetValue);
    if (!Number.isFinite(target) || target <= 0) {
      setError('Cible numérique strictement positive requise');
      return;
    }
    if (new Date(start).getTime() > new Date(end).getTime()) {
      setError('La date de début doit être antérieure à la date de fin');
      return;
    }
    setSubmitting(true);
    try {
      await objectivesApi.update(token, objective.id, {
        targetValue: target,
        periodStart: start,
        periodEnd: end,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur');
      setSubmitting(false);
    }
  }

  return (
    <Modal title={`Modifier · ${autoLabel(objective)}`} onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div className="rounded-md border border-border-subtle bg-gray-50 p-3 text-xs text-text-muted">
          Type ({OBJECTIVE_TYPE_LABELS[objective.type]}) et attribution sont figés après création.
          Seules la cible et les bornes de période peuvent être ajustées.
        </div>
        <div>
          <Label htmlFor="obj-edit-target" required>
            Cible ({targetUnit(objective.type) === 'euros' ? '€' : 'nombre'})
          </Label>
          <Input
            id="obj-edit-target"
            type="number"
            min={1}
            value={targetValue}
            onChange={(e) => setTargetValue(e.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="obj-edit-start">Début</Label>
            <Input
              id="obj-edit-start"
              type="date"
              value={toInputDate(start)}
              onChange={(e) => setStart(new Date(`${e.target.value}T00:00:00.000Z`).toISOString())}
            />
          </div>
          <div>
            <Label htmlFor="obj-edit-end">Fin</Label>
            <Input
              id="obj-edit-end"
              type="date"
              value={toInputDate(end)}
              onChange={(e) => setEnd(new Date(`${e.target.value}T23:59:59.999Z`).toISOString())}
            />
          </div>
        </div>
        {error && <Banner tone="danger">{error}</Banner>}
        <ModalFooter>
          <Button variant="secondary" onClick={onClose} type="button">
            Annuler
          </Button>
          <Button variant="primary" type="submit" loading={submitting}>
            Enregistrer
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Modale helper - dupliquée depuis ConfigPage pour ne pas dépendre d'un
// export croisé. Même comportement (focus trap, escape, click outside).
// ---------------------------------------------------------------------------

function Modal({
  onClose,
  title,
  children,
  wide,
}: {
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  wide?: boolean;
}) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  useFocusTrap(true, dialogRef);
  useHotkey('escape', onClose, { allowInInput: true });
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-brand/30 px-4 backdrop-blur-sm animate-fade-in"
      role="presentation"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        className={`w-full rounded-lg bg-white shadow-modal animate-scale-in ${wide ? 'max-w-3xl' : 'max-w-md'}`}
      >
        <div className="flex items-center justify-between border-b border-border-subtle px-6 py-4">
          <h2 className="text-lg font-semibold text-brand">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-text-muted hover:bg-gray-100 hover:text-brand"
            aria-label="Fermer"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function ModalFooter({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex justify-end gap-2 border-t border-border-subtle pt-4">{children}</div>
  );
}
